import { useQueries } from '@tanstack/react-query';

import { FIVE_MINUTES } from '@features/state/QueryClient/QueryClient';
import { useGetRestaurants } from '@features/tables/hooks/useGetRestaurants';
import {
  getTablesAvailability,
  getTablesAvailabilityKey,
} from '@features/tables/hooks/useGetTablesAvailability';
import { MappedRestaurantsType } from '@features/tables/schemas/MappedRestaurantResponse.schema';
import {
  PromotionsType,
  TablesAvailabilityType,
  TimeSlotsType,
} from '@features/tables/schemas/TablesAvailability.schema';

import { usePackageConfiguration } from '../../contexts';
import { getSelectedRestaurantChoice } from '../../helpers/diningRestaurantChoices';
import { DiningDateGroup } from '../../helpers/groupDiningOptionsByDate';
import { filterPromotionsByTimeslots } from '../../helpers/packageDiningMenus';
import { DiningRestaurantChoice } from '../../types';
import { filterTimeslotsByMenuOrTimeRestriction } from '../TablesStep/helpers/filterTimeslotsByMenuOrTimeRestrictions';

export type PackageDiningOptionAvailability = {
  restaurant?: MappedRestaurantsType[number];
  restaurantChoice?: DiningRestaurantChoice;
  promotions: PromotionsType;
  timeSlots: TimeSlotsType;
  isFetching: boolean;
};

export function usePackageDiningAvailability(
  dateGroups: DiningDateGroup[],
  selectedRestaurantIds: Array<number | undefined> = []
): Record<number, PackageDiningOptionAvailability> {
  const { property, language, journeyParams } = usePackageConfiguration();
  const { data: restaurants } = useGetRestaurants(property.id, language);

  const entries = dateGroups.flatMap(({ date, options }) =>
    options.map(({ option, optionIndex }) => {
      const restaurantChoice = getSelectedRestaurantChoice(
        option,
        selectedRestaurantIds[optionIndex]
      );

      return {
        date,
        option,
        optionIndex,
        restaurantChoice,
        restaurant: restaurantChoice
          ? restaurants?.find(({ config }) => config.id === restaurantChoice.id)
          : undefined,
      };
    })
  );

  const queries = useQueries({
    queries: entries.map(({ date, restaurant }) => {
      const params = {
        propertyId: property.id,
        date,
        restaurant: restaurant?.restaurantName || '',
        restaurantId: restaurant?.config.id,
        adults: journeyParams.adults,
        children: journeyParams.children ?? 0,
      };

      return {
        queryKey: getTablesAvailabilityKey(params),
        queryFn: () => getTablesAvailability(params),
        staleTime: FIVE_MINUTES,
        cacheTime: FIVE_MINUTES,
        enabled: !!restaurant,
      };
    }),
  });

  return Object.fromEntries(
    entries.map(({ optionIndex, option, restaurantChoice, restaurant }, index) => {
      const query = queries[index];
      const data = query?.data as TablesAvailabilityType | undefined;
      const timeSlots = filterTimeslotsByMenuOrTimeRestriction(data?.timeSlots || [], {
        ...option,
        ...restaurantChoice,
      });

      return [
        optionIndex,
        {
          restaurant,
          restaurantChoice,
          promotions: filterPromotionsByTimeslots(data?.promotions || [], timeSlots),
          timeSlots,
          isFetching: !!restaurant && !!query?.isFetching,
        },
      ];
    })
  );
}
